import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import './UserWaitlistDecisionModal.scss';

import { ReactComponent as InstagramIcon } from '../../static/images/social_icons/instagram.svg';
import { ReactComponent as TiktokIcon } from '../../static/images/social_icons/tiktok.svg';
import { ReactComponent as YoutubeIcon } from '../../static/images/social_icons/youtube.svg';
import { ReactComponent as PinterestIcon } from '../../static/images/social_icons/pinterest.svg';
import { ReactComponent as SubstackIcon } from '../../static/images/social_icons/substack.svg';
import { ReactComponent as LinkIcon } from '../../static/images/social_icons/link.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-solid-svg-icons';
import { copyToClipboard } from '../../helpers/helpers';
import { parseWaitlistAutomationData, getWaitlistLegalNameIfDifferentFromName } from '../../helpers/waitlist_helpers';
import Modal from '../General/Modal';
import Tooltip from '../General/Tooltip';

import { fetchRecommendedRegistrationCodes, fetchWaitlistApplicantAccount } from '../../api/waitlist';

const PLATFORM_ICONS = {
  Instagram: InstagramIcon,
  TikTok: TiktokIcon,
  Youtube: YoutubeIcon,
  Pinterest: PinterestIcon,
  Substack: SubstackIcon
};

const UserWaitlistDecisionModal = props => {
  const debouncerRef = useRef(null);
  const { close, waitlistUser } = props;
  const { name, email, ShopperUser_id, largestFollowingPlatform, largestFollowing } = waitlistUser;
  const { isScammer, isScammerReason, automationDecisionReason, automationDecision } = waitlistUser;
  const legalName = getWaitlistLegalNameIfDifferentFromName(waitlistUser);

  const [suggestedCodes, setSuggestedCodes] = useState([]);
  const [code, setCode] = useState('');
  const [notes, setNotes] = useState(waitlistUser.notes || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applicantAccount, setApplicantAccount] = useState({});
  const { matchedShopper, existingAccount } = applicantAccount;
  const isShopperUpgrade = !!ShopperUser_id || !!matchedShopper;
  const isDecided = !!waitlistUser.isAccepted || !!waitlistUser.isRejected;

  useEffect(() => {
    fetchRecommendedRegistrationCodes(name).then(res => setSuggestedCodes(res?.suggestedCodes || []));
  }, [name]);

  useEffect(() => {
    if (ShopperUser_id || isDecided) return;
    fetchWaitlistApplicantAccount(waitlistUser)
      .then(res => setApplicantAccount(res || {}))
      .catch(err => cogoToast.error(err || 'Error checking for an existing account'));
  }, [waitlistUser.id, ShopperUser_id, isDecided]);

  useEffect(() => {
    if (notes === null || notes === undefined) return;
    else if (!waitlistUser?.id) return;
    else if (notes === waitlistUser.notes) return;

    if (debouncerRef.current) clearTimeout(debouncerRef.current);
    debouncerRef.current = setTimeout(() => {
      props.updateWaitlistUser(waitlistUser, { notes }).catch(err => cogoToast.error(err || 'Error saving note'));
    }, 1000);
  }, [notes, waitlistUser]);

  const acceptUserOffline = async () => {
    if (!code) return cogoToast.error('Please enter a code for the creator to register with.');
    setIsSubmitting(true);
    try {
      await props.acceptUser(waitlistUser, { code, handledOffline: true, isAccepted: true, isRejected: false });
      cogoToast.success(`Accepted ${name}, handling offline`);
      close();
    } catch (err) {
      cogoToast.error(err || 'Error accepting user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const acceptUser = async () => {
    if (!code && !isShopperUpgrade) return cogoToast.error('Please enter a code for the creator to register with.');
    setIsSubmitting(true);
    try {
      const decision = matchedShopper
        ? { isAccepted: true, isRejected: false, upgradeMatchedShopper: true }
        : { code, isAccepted: true, isRejected: false };
      const newUser = await props.acceptUser(waitlistUser, decision);
      if (newUser) {
        isShopperUpgrade
          ? cogoToast.success(`Upgraded the account of ${name} to a full creator account.`)
          : cogoToast.success(`Accepted ${name}, sent code ${code} to ${email}`);
        close();
      }
    } catch (err) {
      cogoToast.error(err || 'Error accepting user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const rejectUser = async () => {
    setIsSubmitting(true);
    try {
      await props.rejectWaitlistApplication(waitlistUser);
      close();
    } catch (err) {
      cogoToast.error(err || 'Error rejecting user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const markAsScammer = async () => {
    setIsSubmitting(true);
    try {
      await props.markApplicationAsScammer(waitlistUser);
      close();
    } catch (err) {
      cogoToast.error(err || 'Error marking user as scammer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { automationData, gpt, acceptConfidence, rejectConfidence } = parseWaitlistAutomationData(waitlistUser);
  const webEnrichment = automationData?.webEnrichment;
  const isCountryReview = waitlistUser.needsReview && automationData?.gate === 'country_raised_threshold';

  const aiRecommendation =
    gpt?.scammerLikelihood > 60
      ? { type: 'scammer', label: `Scammer (${gpt.scammerLikelihood}% likelihood)`, reason: gpt.reason, rating: gpt.rating }
      : waitlistUser.needsReview || gpt?.requiresManualReview
      ? { type: 'review', label: isCountryReview ? 'Country Review Required' : 'Needs Manual Review', reason: automationDecisionReason || gpt?.reason, rating: gpt?.rating }
      : acceptConfidence > 60
      ? { type: 'accept', label: `Accept (${acceptConfidence}% confidence)`, reason: gpt.reason, rating: gpt.rating }
      : rejectConfidence > 60
      ? { type: 'reject', label: `Reject (${rejectConfidence}% confidence)`, reason: gpt.reason, rating: gpt.rating }
      : automationData?.missingData
      ? { type: 'no-data', label: 'No Social Data Found', reason: automationData.reason, rating: null }
      : automationDecisionReason
      ? { type: automationDecision ? 'accept' : 'reject', label: automationDecision ? 'Recommend Accept' : 'Recommend Reject', reason: automationDecisionReason, rating: null }
      : null;

  const socialFields = [
    { website: waitlistUser.instagramHandle ? `https://instagram.com/${waitlistUser.instagramHandle}` : null, icon: InstagramIcon },
    { website: waitlistUser.tiktokHandle ? `https://tiktok.com/@${waitlistUser.tiktokHandle}` : null, icon: TiktokIcon },
    { website: waitlistUser.youtubeHandle ? `https://youtube.com/@${waitlistUser.youtubeHandle}` : null, icon: YoutubeIcon },
    { website: waitlistUser.pinterestHandle ? `https://pinterest.com/${waitlistUser.pinterestHandle}` : null, icon: PinterestIcon },
    { website: waitlistUser.substackHandle ? `https://${waitlistUser.substackHandle}.substack.com` : null, icon: SubstackIcon },
    { website: waitlistUser.website ? `https://${waitlistUser.website}` : null, icon: LinkIcon }
  ];

  const authenticatedFields = [
    {
      platform: 'instagram',
      connected: waitlistUser.linkedSocialAccounts?.find(l => l.type === 'instagram'),
      icon: InstagramIcon,
      getUrl: c => c.instagramUsername ? `https://instagram.com/${c.instagramUsername}` : null,
      getTooltip: c => c.instagramUsername ? `@${c.instagramUsername}` : 'Instagram account connected (no url available)'
    },
    {
      platform: 'tiktok',
      connected: waitlistUser.linkedSocialAccounts?.find(l => l.type === 'tiktok'),
      icon: TiktokIcon,
      getUrl: c => c.tiktokUsername ? `https://tiktok.com/@${c.tiktokUsername}` : null,
      getTooltip: c => c.tiktokUsername ? `@${c.tiktokUsername}` : 'TikTok account connected (no url available)'
    },
    {
      platform: 'youtube',
      connected: waitlistUser.linkedSocialAccounts?.find(l => l.type === 'youtube'),
      icon: YoutubeIcon,
      getUrl: c => c.youtubeUsername ? `https://youtube.com/@${c.youtubeUsername}` : c.youtubeChannelId ? `https://youtube.com/channel/${c.youtubeChannelId}` : null,
      getTooltip: c => c.youtubeUsername ? `@${c.youtubeUsername}` : 'YouTube account connected (no url available)'
    }
  ];

  const PlatformIcon = PLATFORM_ICONS[largestFollowingPlatform];

  return (
    <Modal close={close}>
      <div className='wdm-outer'>
        <div className='wdm-inner'>

          {/* Header */}
          <div className='wdm-header'>
            <div className='wdm-header-main'>
              <div className='wdm-title'>{name}</div>
              {legalName && <div className='wdm-legal-name'>Legal name: {legalName}</div>}
              <div className='wdm-email' onClick={() => copyToClipboard(email, true)}>
                {email}
                <FontAwesomeIcon icon={faCopy} className='wdm-copy-icon' />
              </div>
            </div>
            <div className='wdm-tags'>
              {PlatformIcon && (
                <span className='wdm-tag'>
                  <PlatformIcon className='wdm-tag-icon' />
                  {largestFollowing}
                </span>
              )}
              <span className={cn('wdm-tag', { muted: !waitlistUser.hasPublicSocialMedia })}>
                {waitlistUser.hasPublicSocialMedia ? 'Public' : 'Private'}
              </span>
              {isShopperUpgrade && <span className='wdm-tag'>Shopper</span>}
              {!!isScammer && (
                <Tooltip message={isScammerReason}>
                  <span className='wdm-tag error'>Scammer</span>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Socials */}
          <div className='wdm-socials'>
            <div className='wdm-socials-links'>
              {socialFields.map((field, i) => {
                const { website, icon: Icon } = field;
                return (
                  <div key={i} className={cn('wdm-social-icon', { disabled: !website })}>
                    {website ? (
                      <Tooltip message={website}>
                        <a href={website} target='_blank' rel='noopener noreferrer'>
                          <Icon />
                        </a>
                      </Tooltip>
                    ) : (
                      <Icon />
                    )}
                  </div>
                );
              })}
            </div>
            <div className='wdm-socials-auth'>
              <span className='wdm-socials-auth-label'>Connected</span>
              {authenticatedFields.map((field, i) => {
                const { connected, icon: Icon, getUrl, getTooltip } = field;
                const url = connected ? getUrl(connected) : null;
                return (
                  <div key={i} className={cn('wdm-social-icon sm', { disabled: !connected })}>
                    {connected ? (
                      <Tooltip message={getTooltip(connected)}>
                        {url ? (
                          <a href={url} target='_blank' rel='noopener noreferrer'>
                            <Icon />
                          </a>
                        ) : (
                          <Icon />
                        )}
                      </Tooltip>
                    ) : (
                      <Icon />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* AI Recommendation */}
          {!!aiRecommendation && (
            <div className={`wdm-ai ${aiRecommendation.type}`}>
              <div className='wdm-ai-header'>
                <span className='wdm-ai-label'>{aiRecommendation.label}</span>
                {aiRecommendation.rating && (
                  <span className='wdm-ai-rating'>{aiRecommendation.rating}/5</span>
                )}
              </div>
              <div className='wdm-ai-reason'>{aiRecommendation.reason}</div>
            </div>
          )}

          {/* Web presence dossier */}
          {!!webEnrichment && (
            <div className='wdm-info'>
              <div className='wdm-info-label'>
                Web Presence{webEnrichment.foundEvidence ? ` · ${webEnrichment.identityConfidence}% identity match` : ' · nothing tied to this person'}
              </div>
              <div className='wdm-info-content'>
                {webEnrichment.summary}
                {webEnrichment.occupationSignals?.length > 0 && <div>Signals: {webEnrichment.occupationSignals.join(' · ')}</div>}
                {webEnrichment.redFlags?.length > 0 && <div>Red flags: {webEnrichment.redFlags.join(' · ')}</div>}
                {webEnrichment.handleFindings?.map((finding, i) => (
                  <div key={`handle-${i}`}>
                    Handle check · {finding.platform} @{finding.submittedHandle}{' '}
                    {finding.exactMatchFound ? 'exists' : finding.foundHandle ? `not found · likely @${finding.foundHandle}` : 'not found'}
                    {!!finding.profileUrl && (
                      <>
                        {' '}
                        <a href={finding.profileUrl} target='_blank' rel='noopener noreferrer'>
                          profile
                        </a>
                      </>
                    )}
                    {finding.note ? ` · ${finding.note}` : ''}
                  </div>
                ))}
                {/* Below 50 the links are same-name guesses more often than not — showing them misleads faster than it helps. */}
                {webEnrichment.identityConfidence >= 50 &&
                  webEnrichment.corroboratingLinks?.map((link, i) => (
                    <div key={i}>
                      <a href={link.url} target='_blank' rel='noopener noreferrer'>
                        {link.title || link.url}
                      </a>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Info sections */}
          {!!(matchedShopper || existingAccount) && (
            <div className={cn('wdm-info', { error: !!existingAccount })}>
              <div className='wdm-info-label'>{matchedShopper ? 'Email matches a shopper account' : 'Email already has an account'}</div>
              <div className='wdm-info-content'>
                <a href={`https://shopmy.us/${(matchedShopper || existingAccount).username}`} target='_blank' rel='noopener noreferrer'>
                  {(matchedShopper || existingAccount).name} · @{(matchedShopper || existingAccount).username}
                </a>
                {existingAccount?.isBanned ? ' · Banned' : existingAccount?.isArchived ? ' · Archived' : ''}
                <div>
                  {matchedShopper
                    ? "The application's email isn't verified. Upgrading applies its socials and phone to this account and credits no referrer, so check it's the same person."
                    : 'No signup code can be sent to this email. Use Reject Without Email to close the application.'}
                </div>
              </div>
            </div>
          )}

          {!!isScammerReason && (
            <div className='wdm-info error'>
              <div className='wdm-info-label'>Flagged as Scammer</div>
              <div className='wdm-info-content'>{isScammerReason}</div>
            </div>
          )}

          {!!waitlistUser.pitch && (
            <div className='wdm-info'>
              <div className='wdm-info-label'>Pitch</div>
              <div className='wdm-info-content'>{waitlistUser.pitch}</div>
            </div>
          )}

          <div className='wdm-info'>
            <div className='wdm-info-label'>How they heard about ShopMy</div>
            <div className='wdm-info-content'>{waitlistUser.howTheyHeard || '—'}</div>
          </div>

          <div className='wdm-info'>
            <div className='wdm-info-label'>Referred by</div>
            <div className='wdm-info-content'>
              {waitlistUser.referringUser?.id ? (
                <a href={`https://shopmy.us/${waitlistUser.referringUser.username}`} target='_blank' rel='noopener noreferrer'>
                  {waitlistUser.referringUser.name} · Creator Referral
                </a>
              ) : waitlistUser.referringBrand?.id ? (
                `${waitlistUser.referringBrand.name} · Brand Referral`
              ) : (
                '—'
              )}
            </div>
          </div>

          {/* Notes */}
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder='Add a note for this applicant…'
            className='wdm-notes'
          />

          {/* Registration code */}
          {!isShopperUpgrade && !existingAccount && (
            <div className='wdm-codes'>
              <div className='wdm-codes-label'>Suggested registration codes</div>
              {suggestedCodes.length > 0 && (
                <div className='wdm-codes-chips'>
                  {suggestedCodes.map(c => (
                    <button
                      key={c}
                      className={cn('wdm-code-chip', { active: code === c })}
                      onClick={() => setCode(c)}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
              <input
                className='wdm-code-input'
                type='text'
                placeholder='Registration code'
                value={code}
                onChange={e => setCode(e.target.value)}
              />
            </div>
          )}

          {/* Actions */}
          <div className='wdm-actions'>
            {isDecided ? (
              <>
                <button className='wdm-btn' onClick={close}>Close</button>
                <button className='wdm-btn primary disabled' disabled>
                  {waitlistUser.isAccepted ? 'Already Accepted' : 'Already Rejected'}
                </button>
              </>
            ) : isShopperUpgrade ? (
              <>
                <button className='wdm-btn' onClick={close}>Cancel</button>
                <button className={cn('wdm-btn danger-outline', { disabled: isSubmitting })} disabled={isSubmitting} onClick={rejectUser}>Reject Upgrade</button>
                <button className={cn('wdm-btn primary', { disabled: isSubmitting })} disabled={isSubmitting} onClick={acceptUser}>Upgrade to Creator</button>
              </>
            ) : existingAccount ? (
              <>
                <button className='wdm-btn' onClick={close}>Cancel</button>
                <button className={cn('wdm-btn danger', { disabled: isSubmitting })} disabled={isSubmitting} onClick={markAsScammer}>Scammer</button>
                <button className={cn('wdm-btn danger-outline', { disabled: isSubmitting })} disabled={isSubmitting} onClick={rejectUser}>Reject</button>
              </>
            ) : (
              <>
                <button className='wdm-btn' onClick={close}>Cancel</button>
                <button className={cn('wdm-btn danger', { disabled: isSubmitting })} disabled={isSubmitting} onClick={markAsScammer}>Scammer</button>
                <button className={cn('wdm-btn danger-outline', { disabled: isSubmitting })} disabled={isSubmitting} onClick={rejectUser}>Reject</button>
                <button className={cn('wdm-btn secondary', { disabled: isSubmitting })} disabled={isSubmitting} onClick={acceptUserOffline}>Handle Offline</button>
                <button className={cn('wdm-btn primary', { disabled: isSubmitting })} disabled={isSubmitting} onClick={acceptUser}>Send Code</button>
              </>
            )}
          </div>

        </div>
      </div>
    </Modal>
  );
};

UserWaitlistDecisionModal.propTypes = {
  close: PropTypes.func.isRequired,
  waitlistUser: PropTypes.object.isRequired,
  acceptUser: PropTypes.func.isRequired,
  updateWaitlistUser: PropTypes.func.isRequired,
  rejectWaitlistApplication: PropTypes.func.isRequired,
  markApplicationAsScammer: PropTypes.func.isRequired
};

export default UserWaitlistDecisionModal;
